
import React, { memo, useMemo } from 'react';
import MessageStatusMC from './MessageStatusMC';
import GroupSenderMC from './GroupSenderMC';
import CaptionMC from './CaptionMC';
import DocumentMC from './DocumentMC';
import { formatText } from '../../../../util/textUtil';

/**
 * @typedef {Object} Message
 * @property {string} id - ID único del mensaje
 * @property {string} [contentMediaCaption] - Caption/descripción del documento
 * @property {string} [fileName] - Nombre del archivo del documento
 * @property {string} [contentMediaUrl] - URL del documento para descarga
 * @property {boolean} inbound - Si el mensaje es entrante o saliente
 * @property {string} platform - Plataforma del mensaje
 * @property {number} timestamp - Timestamp del mensaje
 */

/**
 * @typedef {Object} Conversation
 * @property {string} id - ID único de la conversación
 * @property {string} platform - Plataforma ('whatsapp', 'messenger', 'instagram')
 * @property {string} customer_id - ID del cliente asociado
 */

/**
 * @typedef {Object} DocumentMessageMCProps
 * @property {Conversation} conversation - Objeto conversación que contiene el mensaje
 * @property {Message} message - Objeto mensaje de tipo documento
 */

/**
 * Componente optimizado para renderizar mensajes de documento en el inbox.
 * 
 * Renderiza documentos (PDF, DOC, XLS, etc.) con caption opcional, estado del mensaje,
 * información del remitente y funcionalidad de descarga integrada.
 * 
 * @component
 * @memberof InboxComponents
 * @category Molecules
 * 
 * @description Características principales:
 * - Renderizado optimizado con React.memo para evitar re-renders innecesarios
 * - Memoización del caption formateado para mejor performance
 * - Soporte multi-plataforma (WhatsApp, Messenger, Instagram)
 * - Integración con componentes atómicos siguiendo Atomic Design
 * - ID único para testing y debugging
 * 
 * @param {DocumentMessageMCProps} props - Props del componente
 * @param {Conversation} props.conversation - Conversación que contiene el mensaje
 * @param {Message} props.message - Mensaje de documento a renderizar
 * 
 * @returns {React.ReactElement} Componente renderizado de mensaje de documento
 * 
 * @example
 * ```jsx
 * import DocumentMessageMC from 'presentation/modules/inbox/components/DocumentMessageMC';
 * 
 * const conversation = {
 *   id: 'conv-123',
 *   platform: 'whatsapp',
 *   customer_id: 'customer-456'
 * };
 * 
 * const message = {
 *   id: 'msg-789',
 *   contentMediaCaption: 'Factura del pedido #123',
 *   fileName: 'factura_123.pdf',
 *   contentMediaUrl: 'https://storage.example.com/factura_123.pdf',
 *   inbound: true,
 *   platform: 'whatsapp',
 *   timestamp: 1699276800000
 * };
 * 
 * <DocumentMessageMC 
 *   conversation={conversation} 
 *   message={message} 
 * />
 * ```
 * 
 * @example
 * // Documento sin caption
 * const messageWithoutCaption = {
 *   id: 'msg-790',
 *   fileName: 'contrato.pdf',
 *   contentMediaUrl: 'https://storage.example.com/contrato.pdf',
 *   inbound: false,
 *   platform: 'messenger'
 * };
 * 
 * <DocumentMessageMC 
 *   conversation={conversation} 
 *   message={messageWithoutCaption} 
 * />
 * 
 * @performance
 * - React.memo previene re-renders cuando props no cambian
 * - useMemo en caption evita re-formateo innecesario del texto
 * - Componentes hijos optimizados individualmente
 * - ID único para identificación eficiente en listas virtuales
 * 
 * @accessibility
 * - ID único para screen readers y testing
 * - Estructura semántica con divs contenedores
 * - Caption formateado preserva legibilidad
 * 
 * @see {@link DocumentMC} Componente de descarga de documento
 * @see {@link CaptionMC} Componente de caption formateado
 * @see {@link MessageStatusMC} Componente de estado del mensaje
 * @see {@link GroupSenderMC} Componente de información del remitente
 * 
 * @since 1.0.0
 * @version 2.0.0 - Optimized with memoization and better performance
 */
const DocumentMessageMC = memo(({ conversation, message }) => {
  /**
   * Caption memoizado del documento para optimizar performance.
   * 
   * Formatea el texto del caption usando la utilidad formatText,
   * preservando enlaces, menciones y formato especial.
   * Se memoiza para evitar re-formateo innecesario en cada render.
   * 
   * @type {string}
   * @memoized
   * @dependency {string} message?.contentMediaCaption
   * 
   * @example
   * // Input: "Revisa este PDF: https://example.com/doc.pdf"
   * // Output: "Revisa este PDF: <a href='https://example.com/doc.pdf'>https://example.com/doc.pdf</a>"
   */
  const caption = useMemo(() => {
    return formatText(message?.contentMediaCaption || '');
  }, [message?.contentMediaCaption]);

  return (
    <div
      id={`doc-message-${message.id}`}
      className="tw-wrapper tw-text-default"
    >
      <GroupSenderMC message={message} />
      <DocumentMC
        data={message}
        platform={conversation.platform}
        fileName={message.fileName}
      />
      <CaptionMC caption={caption} />
      <MessageStatusMC message={message} platform={conversation.platform} />
    </div>
  );
});

DocumentMessageMC.displayName = 'DocumentMessageMC';

export default DocumentMessageMC;
